import React from 'react';
import { StarFilled } from '@ant-design/icons';
import { Alert, Badge, Button, Card, Col, Collapse, Empty, Row, Space, Spin, Tag, Typography } from 'antd';
import { Dayjs } from 'dayjs';

import LatestMeasurementCard from './LatestMeasurementCard';
import MeasurementSummaryCard from './MeasurementSummaryCard';
import {
    DashboardCollapseItem,
    DashboardMetricDisplay,
    DashboardWarning,
    LatestMeasurement,
} from './dashboardTypes';

const { Text } = Typography;

type DashboardOperationalViewProps = {
    warnings: DashboardWarning[];
    warningsToShow: DashboardWarning[];
    showAllWarnings: boolean;
    onToggleShowAllWarnings: () => void;
    onHideWarning: (warningId: number) => Promise<void> | void;
    onDeleteWarning: (warningId: number) => Promise<void> | void;
    onWarningNavigate: (warning: DashboardWarning) => void;
    companyId: number;
    preferredMetricKeys: string[];
    onMeasurementNavigate: (measurementId: string | number, areaId?: number | null, rangeOverride?: [Dayjs, Dayjs]) => void;
    onAreaNavigate: (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => void;
    favoriteMeasurement: LatestMeasurement | null;
    loadingFavoriteMeasurement: boolean;
    favoriteAreaMetrics: DashboardMetricDisplay[];
    favoriteMeasurementRange: [Dayjs, Dayjs] | null;
    templateImageUrl: string | null;
    loadingInsights: boolean;
    hasMostVisited: boolean;
    mostVisitedCollapseItems: DashboardCollapseItem[];
};

const DashboardOperationalView: React.FC<DashboardOperationalViewProps> = ({
    warnings,
    warningsToShow,
    showAllWarnings,
    onToggleShowAllWarnings,
    onHideWarning,
    onDeleteWarning,
    onWarningNavigate,
    companyId,
    preferredMetricKeys,
    onMeasurementNavigate,
    onAreaNavigate,
    favoriteMeasurement,
    loadingFavoriteMeasurement,
    favoriteAreaMetrics,
    favoriteMeasurementRange,
    templateImageUrl,
    loadingInsights,
    hasMostVisited,
    mostVisitedCollapseItems,
}) => {
    return (
        <>
            {warnings.length > 0 && (
                <Card
                    title="Warnings"
                    className="dashboard-insights-card"
                    style={{ marginBottom: 24 }}
                    extra={
                        warnings.length > 1 ? (
                            <Button type="link" onClick={onToggleShowAllWarnings}>
                                <Badge count={warnings.length} />
                                <span style={{ marginLeft: 8 }}>{showAllWarnings ? 'Collapse' : 'Show all'}</span>
                            </Button>
                        ) : null
                    }
                >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {warningsToShow.map((warning) => (
                            <Alert
                                key={warning.id}
                                type="warning"
                                showIcon
                                className="report-warning-banner"
                                message={
                                    <Space
                                        size="middle"
                                        wrap={false}
                                        className="dashboard-warning-message"
                                        style={{
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Space size={12} wrap style={{ minWidth: 0 }} className="dashboard-warning-meta">
                                            <Tag color="red">Out of range</Tag>
                                            <Text strong style={{ whiteSpace: 'nowrap' }}>
                                                {warning.metricName}
                                            </Text>
                                            <Text style={{ whiteSpace: 'nowrap' }}>
                                                {warning.field}: {Number(warning.value).toFixed(2)} {warning.unit || ''}
                                            </Text>
                                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                                                {warning.areaPath || warning.areaName}
                                            </Text>
                                        </Space>
                                        <Space size="small" className="dashboard-warning-actions">
                                            <Button
                                                size="small"
                                                type="primary"
                                                onClick={() => {
                                                    void onHideWarning(warning.id);
                                                    onWarningNavigate(warning);
                                                }}
                                            >
                                                View report
                                            </Button>
                                            <Button size="small" onClick={() => onHideWarning(warning.id)}>
                                                Hide
                                            </Button>
                                            <Button size="small" danger onClick={() => onDeleteWarning(warning.id)}>
                                                Delete
                                            </Button>
                                        </Space>
                                    </Space>
                                }
                            />
                        ))}
                    </Space>
                </Card>
            )}

            <div className="dashboard-latest-measurement-section">
                <LatestMeasurementCard
                    companyId={companyId}
                    preferredMetricKeys={preferredMetricKeys}
                    onMeasurementClick={onMeasurementNavigate}
                    onAreaClick={onAreaNavigate}
                />
            </div>

            {favoriteMeasurement && !loadingFavoriteMeasurement && (
                <div className="dashboard-favorite-section" style={{ marginTop: 24 }}>
                    <MeasurementSummaryCard
                        title={
                            <Space size="small">
                                <StarFilled style={{ color: '#fadb14', fontSize: 18 }} />
                                <Text strong>Favorite area: {favoriteMeasurement.areaName || 'Favorite area'}</Text>
                            </Space>
                        }
                        measurement={favoriteMeasurement}
                        templateImageUrl={templateImageUrl}
                        metricsToShow={favoriteAreaMetrics}
                        onMeasurementClick={onMeasurementNavigate}
                        onAreaClick={onAreaNavigate}
                        showReportCta
                        reportDateRange={favoriteMeasurementRange}
                        areaId={favoriteMeasurement.areaId ?? null}
                        dayMeasurements={favoriteMeasurement.dayMeasurements || []}
                    />
                </div>
            )}

            <Row gutter={[24, 24]} className="dashboard-insights-grid">
                <Col xs={24} xl={24}>
                    <Card title="Most visited (last 7 days)" className="dashboard-insights-card">
                        {loadingInsights ? (
                            <Spin />
                        ) : !hasMostVisited ? (
                            <Empty description="No recent visits tracked." />
                        ) : (
                            <Collapse accordion items={mostVisitedCollapseItems} />
                        )}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default DashboardOperationalView;
